import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import styled from "styled-components";

import {
  buildNetworkStatusMessage,
  buildOutOfNetworkExceptionMessage,
} from "../eligibilityMessages";

const StyledPill = styled.span<{ $tone: "purple" | "red" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  background: ${({ $tone }) =>
    `color-mix(in srgb, var(${
      $tone === "purple" ? "--brand-purple-600" : "--red-600"
    }) 8%, var(--brand-white))`};
  border: ${({ $tone }) =>
    `1px solid color-mix(in srgb, var(${
      $tone === "purple" ? "--brand-purple-600" : "--red-600"
    }) 25%, var(--brand-white))`};
  color: ${({ $tone }) =>
    $tone === "purple" ? "var(--brand-purple-700)" : "var(--red-600)"};
`;

/** Read-only summary of the plan from the stored eligibility result. Changes no form values. */
export function PlanCoverageCard({
  benefitDetails,
  className = "mx-3 mt-3",
}: {
  benefitDetails: any;
  /** Outer margins; the create page keeps the default, the order view dialog passes its own. */
  className?: string;
}) {
  const response = benefitDetails?.response;
  const summary = response?.planCoverageSummary;
  if (!summary) return null;

  const practiceTypeCode = benefitDetails?.request?.practiceTypeCode;
  const serviceType =
    mapPayerNameToCodeForDropdown.find(
      (option) => option.value === practiceTypeCode
    )?.name || practiceTypeCode;
  const isOutOfNetwork =
    !!buildNetworkStatusMessage(response?.networkStatusOverride) &&
    !buildOutOfNetworkExceptionMessage(response?.outOfNetworkException);
  const title =
    summary.PlanNetworkName ||
    summary.PlanName ||
    response?.payerName ||
    "Plan coverage";
  const subtitle = [
    summary.PolicyType,
    summary.GroupNumber && `Group ${summary.GroupNumber}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const fields = [
    { label: "Payer", value: response?.payerName, size: "md:col-6 lg:col-3" },
    {
      label: "Effective",
      value: summary.EffectiveDate,
      size: "md:col-6 lg:col-3",
    },
    {
      label: "Termination",
      value: summary.ExpiryDate,
      size: "md:col-6 lg:col-3",
    },
    { label: "Service type", value: serviceType, size: "md:col-6 lg:col-3" },
    {
      label: "Policy type",
      value: summary.PolicyType,
      size: "md:col-6 lg:col-3",
    },
    { label: "Plan name", value: summary.PlanName, size: "" },
  ];

  return (
    <div
      className={`${className} p-4 border-1 border-round-2xl bg-white`}
      style={{ borderColor: "var(--brand-gray-200)" }}
      data-testid="planCoverageCard"
    >
      <div className="flex align-items-start gap-3">
        <div className="flex flex-column gap-1 flex-1 min-w-0">
          <span
            className="text-base font-bold"
            style={{ color: "var(--brand-black)" }}
          >
            {title}
          </span>
          {subtitle && (
            <span
              className="text-xs"
              style={{ color: "var(--brand-gray-600)" }}
            >
              {subtitle}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap justify-content-end">
          {summary.Status && (
            <StyledPill $tone={summary.Status === "Active" ? "purple" : "red"}>
              <i
                className={
                  summary.Status === "Active"
                    ? "pi pi-check-circle"
                    : "pi pi-times-circle"
                }
              />
              {summary.Status}
            </StyledPill>
          )}
          <StyledPill $tone={isOutOfNetwork ? "red" : "purple"}>
            {isOutOfNetwork ? "Out of network" : "In network"}
          </StyledPill>
        </div>
      </div>
      <div
        className="border-top-1 my-3"
        style={{ borderColor: "var(--brand-gray-200)" }}
      />
      <div className="grid">
        {fields.map(({ label, value, size }) => (
          <div key={label} className={`col-12 ${size} flex flex-column gap-1`}>
            <span
              className="text-xs uppercase"
              style={{
                color: "var(--brand-gray-600)",
                letterSpacing: "0.04em",
              }}
            >
              {label}
            </span>
            <span
              className="text-sm font-medium"
              style={{ color: "var(--brand-black)", overflowWrap: "anywhere" }}
            >
              {value || "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
